
import $ from 'jquery';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import {
  GroupSurveyExtension,
  GroupSurveyExtensionUiClient,
  GroupSurveyUserPresence,
  GroupUiClient,
  GroupUserExternal,
  ListItem,
  SurveyResultMeta,
} from '../../backend/backendapi';
import CenterOnParent from '../../util/CenterOnParent';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { paginationOptions, searchOptions } from '../../../util/vue-good-table';
import { initClient } from '../../../api';
import { formatDateTime } from '../../../util';
import Spinner from '../../util/Spinner';
import PrintBadgeButton from '../group/PrintBadgeButton';
import PresenceStartStopButton from './PresenceStartStopButton.vue';

type Row = {
  userId?: string,
  user: ListItem,
  externalUserId?: string,
  externalUser?: GroupUserExternal,
  email: string,
  firstName: string,
  lastName: string,
  company: string | null,
  allSurveysPassed: boolean,
  validInPeriod: boolean,
  presence: GroupSurveyUserPresence | null,
}

@Component({
  components: {
    CenterOnParent,
    PresenceStartStopButton,
    PrintBadgeButton,
    Spinner,
    VueGoodTable,
  },
})
export default class UserPresenceModal extends Vue {
  group: GroupSurveyExtension | null = null;
  results: SurveyResultMeta[] | null = null;
  loading = false;
  saving: string[] = [];
  private readonly users: ListItem[] = [];

  private readonly groupApi = initClient(GroupUiClient);
  private readonly groupSurveyExtensionApi = initClient(GroupSurveyExtensionUiClient);

  //api

  get rows(): Row[] {
    if (!this.group) {
      return [];
    }

    const users: Row[] = [
      ...this.group.group!.userIds!.map(userId => {
        const user = this.users.find(user => user.id === userId.userId);
        return {
          userId: userId.userId,
          user: user,
          email: user?.email,
          firstName: user?.firstName,
          lastName: user?.lastName,
          company: null,
        } as Row;
      }),
      ...this.group.group!.externalUsers!.map(externalUser => {
        return {
          externalUserId: externalUser.id,
          externalUser: externalUser,
          email: externalUser.email,
          firstName: externalUser.firstName,
          lastName: externalUser.lastName,
          company: null,
          // company: externalUser.company,
        } as Row;
      }),
    ];

    users.forEach((user) => {
      user['allSurveysPassed'] = this.group!.surveyMappings
          .every(mapping => this.results?.some(result => result.surveyId == mapping.surveyId && result.userEmail === user.email && result.passed));
      user['validInPeriod'] = this.group!.surveyMappings
          .every(mapping => this.results?.some(result => result.surveyId == mapping.surveyId && result.userEmail === user.email && result.validInPeriod));
      const presences = this.group!.userPresences
          .filter(e => (user.userId && e.userId == user.userId)
              || (user.externalUserId && e.groupUserExternalId == user.externalUserId));
      presences.sort((l, r) => r.startAt!.getTime() - l.startAt!.getTime());// sort desc
      user['presence'] = presences.length ? presences[0] : null;
    });

    return users;
  }

  get columnsConfig(): any[] {
    return [{
      label: this.$t('survey.advanceRegistration.userPresenceModal.list.userName').toString(),
      field: (e: Row) => (e.firstName ?? '') + ' ' + (e.lastName ?? ''),
    }, {
      label: this.$t('survey.advanceRegistration.userPresenceModal.list.surveysPassed').toString(),
      field: (e: Row) => e.allSurveysPassed,
      formatFn: (value: boolean) => this.$t(value ? 'yes' : 'no').toString(),
    }, {
      label: this.$t('survey.surveyResultMeta.model.validInPeriod').toString(),
      field: (e: Row) => e.validInPeriod,
      formatFn: (value: boolean) => this.$t(value ? 'yes' : 'no').toString(),
    }, {
      label: this.$t('survey.advanceRegistration.userPresenceModal.list.presenceStart').toString(),
      field: (e: Row) => e.presence?.startAt,
      formatFn: (value: Date | undefined) => !value ? '-' : formatDateTime(value),
      type: 'date',
      isStartColumn: true,
    }, {
      label: this.$t('survey.advanceRegistration.userPresenceModal.list.presenceStartBy').toString(),
      field: (e: Row) => this.usersById.get(e.presence?.startBy ?? ''),
      formatFn: (value: ListItem | undefined) => !value ? '-' : this.formatUser(value),
    }, {
      label: this.$t('survey.advanceRegistration.userPresenceModal.list.presenceEnd').toString(),
      field: (e: Row) => e.presence?.endAt,
      formatFn: (value: Date | undefined) => !value ? '-' : formatDateTime(value),
      type: 'date',
      isEndColumn: true,
    }, {
      label: this.$t('survey.advanceRegistration.userPresenceModal.list.presenceEndBy').toString(),
      field: (e: Row) => this.usersById.get(e.presence?.endBy ?? ''),
      formatFn: (value: ListItem | undefined) => !value ? '-' : this.formatUser(value),
    }, {
      label: this.$i18n.t('general.actions').toString(),
      field: 'actions',
      width: '5em',
    }];
  }

  get loadingFailed(): boolean {
    return this.results === null;
  }

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  get usersAndUnknownUser(): ListItem[] {
    return [...this.users, { id: '00000000-0000-0000-0000-000000000000', firstName: '(' + this.$t('general.unknown') + ')' } as ListItem];
  }

  get usersById(): Map<string, ListItem> {
    return new Map(this.usersAndUnknownUser.map(e => [e.id!, e]));
  }

  mounted(): void {
    this.reloadUsers();
  }

  public open(group: GroupSurveyExtension): void {
    this.group = group;
    this.reloadResults();
    $(this.$refs.modal!).modal('show');
  }

  public dismiss(): void {
    $(this.$refs.modal!).modal('hide');
  }

  formatUser(user: ListItem): string {
    return (user.firstName ?? '') + ' ' + (user.lastName ?? '');
  }

  private async reloadResults(): Promise<void> {
    this.loading = true;
    try {
      this.results = null;
      if (!this.group) {
        return;
      }
      this.results = (await this.groupSurveyExtensionApi.getMostRecentResults(this.group.id))
          .sort((l, r) => (r.date?.getTime() ?? 0) - (l.date?.getTime() ?? 0)); // sort desc by date
    } finally {
      this.loading = false;
    }
  }

  private async reloadUsers(): Promise<void> {
    const list = await this.groupApi.listUsers();
    this.users.splice(0);
    this.users.push(...list);
  }

  async onPresenceStopped(row: Row): Promise<void> {
    row.validInPeriod = false; // todo howto update goodtable, usabiltiy
  }
}
